'use client';

import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as XLSX from 'xlsx';
import { useAIStore } from '../../stores/aiStore';
import { useUploadedFileStore } from '../../stores/uploadedFileStore';
import { useWorkflowStore } from '../../stores/workflowStore';
import {
  Robot,
  X,
  PaperPlaneRight,
  Spinner,
  Lightning,
  CheckCircle,
  ArrowCounterClockwise,
  Upload,
  FileXls,
  Trash,
  ChatTeardropText,
} from '@phosphor-icons/react';

interface AIDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onWorkflowGenerated?: (workflow: string) => void;
}

const EXAMPLE_PROMPTS = [
  '筛选出金额大于 1000 的订单',
  '按部门汇总销售额并排序',
  '去除重复的客户记录',
  '计算每月的增长率',
  '关联两个表并导出',
];

function inferSemanticType(columnName: string): string {
  const lower = columnName.toLowerCase();
  if (lower.includes('id') || lower.includes('编号')) return 'id';
  if (lower.includes('name') || lower.includes('名称')) return 'name';
  if (lower.includes('amount') || lower.includes('price') || lower.includes('金额') || lower.includes('价格')) return 'money';
  if (lower.includes('date') || lower.includes('time') || lower.includes('日期') || lower.includes('时间')) return 'date';
  if (lower.includes('count') || lower.includes('quantity') || lower.includes('数量')) return 'quantity';
  return '';
}

// 解析 Excel/CSV 文件
async function parseFile(file: File): Promise<{
  name: string;
  rowCount: number;
  columns: string[];
  data: Record<string, unknown>[];
}> {
  const ext = file.name.toLowerCase();
  const isCSV = ext.endsWith('.csv');
  
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result;
        if (!content) {
          reject(new Error('文件内容为空'));
          return;
        }

        let data: Record<string, unknown>[] = [];
        let columns: string[] = [];

        if (isCSV) {
          // 解析 CSV
          const text = content as string;
          const lines = text.split('\n').filter(line => line.trim());
          if (lines.length === 0) {
            reject(new Error('CSV 文件为空'));
            return;
          }
          
          // 解析表头
          columns = lines[0].split(',').map(col => col.trim().replace(/^["']|["']$/g, ''));
          
          // 解析数据行
          for (let i = 1; i < lines.length; i++) {
            const values = lines[i].split(',').map(val => val.trim().replace(/^["']|["']$/g, ''));
            const row: Record<string, unknown> = {};
            columns.forEach((col, idx) => {
              row[col] = values[idx] ?? '';
            });
            data.push(row);
          }
        } else {
          // 解析 Excel
          const workbook = XLSX.read(content, { type: 'binary' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 }) as unknown[][];
          
          if (jsonData.length === 0) {
            reject(new Error('Excel 文件为空'));
            return;
          }
          
          columns = (jsonData[0] as string[]).map(col => String(col));
          
          for (let i = 1; i < jsonData.length; i++) {
            const row: Record<string, unknown> = {};
            columns.forEach((col, idx) => {
              row[col] = jsonData[i][idx] ?? '';
            });
            data.push(row);
          }
        }

        resolve({
          name: file.name,
          rowCount: data.length,
          columns,
          data,
        });
      } catch (error) {
        reject(error instanceof Error ? error : new Error('解析文件失败'));
      }
    };
    reader.onerror = () => reject(new Error('读取文件失败'));
    
    if (isCSV) {
      reader.readAsText(file);
    } else {
      reader.readAsBinaryString(file);
    }
  });
}

export function AIDrawer({ isOpen, onClose, onWorkflowGenerated }: AIDrawerProps) {
  const [input, setInput] = useState('');
  const [applied, setApplied] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const {
    parseIntent,
    modifyWorkflow,
    isParsing,
    currentIntent,
    currentPlan,
    generatedWorkflow,
    applyGeneratedWorkflow,
    clear,
    generatedNodes,
  } = useAIStore();

  const { uploadedFile, setUploadedFile, clearFile } = useUploadedFileStore();

  // Focus textarea when drawer opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => textareaRef.current?.focus(), 300);
      setApplied(false);
    }
  }, [isOpen]);

  const handleFileSelect = async (file: File) => {
    const ext = file.name.toLowerCase();
    if (!ext.endsWith('.xlsx') && !ext.endsWith('.xls') && !ext.endsWith('.csv')) {
      return;
    }

    try {
      const parsed = await parseFile(file);
      setUploadedFile({
        id: `file_${Date.now()}`,
        ...parsed,
      });
      // 清除之前的 AI 分析结果
      clear();
      setInput('');
      setApplied(false);
    } catch (error) {
      console.error('解析文件失败:', error);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleRemoveFile = () => {
    clearFile();
    clear();
    setInput('');
    setApplied(false);
    // 同时清空工作流 store
    const workflowStore = useWorkflowStore.getState();
    workflowStore.clearWorkflow();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isParsing || !uploadedFile) return;

    setApplied(false);
    const context = {
      availableTables: [
        {
          name: uploadedFile.name,
          columns: uploadedFile.columns.map((col) => ({
            name: col,
            type: 'string',
            semanticType: inferSemanticType(col),
          })),
        },
      ],
    };

    // 如果已经有生成的节点，使用 modifyWorkflow 在现有基础上修改
    if (generatedNodes.length > 0) {
      await modifyWorkflow(input.trim(), context);
    } else {
      await parseIntent(input.trim(), context);
    }

    if (generatedWorkflow && onWorkflowGenerated) {
      onWorkflowGenerated(generatedWorkflow);
    }
  };

  const handleApply = () => {
    applyGeneratedWorkflow(uploadedFile);
    setApplied(true);
  };

  const handleReset = () => {
    setInput('');
    setApplied(false);
    clear();
  };

  const handleModifyWorkflow = () => {
    // 允许用户继续输入修改需求
    setInput('');
    textareaRef.current?.focus();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/20 backdrop-blur-[2px] z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Drawer panel */}
          <motion.div
            className="fixed top-0 right-0 h-full w-[480px] bg-white border-l border-zinc-200 shadow-2xl shadow-zinc-900/20 z-50 flex flex-col"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 35 }}
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-zinc-100 bg-gradient-to-r from-amber-50 to-orange-50">
              <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Robot className="w-5 h-5 text-amber-600" weight="fill" />
              </div>
              <div className="flex-1">
                <h2 className="text-sm font-semibold text-zinc-900">AI 工作流助手</h2>
                <p className="text-xs text-zinc-500">上传文件，用自然语言描述需求，自动生成流程</p>
              </div>
              <motion.button
                onClick={onClose}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-white/60 transition-colors"
              >
                <X className="w-4 h-4" />
              </motion.button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-5 space-y-5">
                {/* File Upload Section */}
                {!uploadedFile ? (
                  <div className="space-y-3">
                    <p className="text-xs font-medium text-zinc-500">第一步：上传数据文件</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileSelect(f); }}
                      className="hidden"
                    />
                    <div
                      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                      onDragLeave={() => setIsDragging(false)}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                      className={`
                        border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all duration-200
                        ${isDragging ? 'border-amber-400 bg-amber-50' : 'border-zinc-300 hover:border-zinc-400 hover:bg-zinc-50'}
                      `}
                    >
                      <div className="w-14 h-14 rounded-xl bg-amber-100 flex items-center justify-center mx-auto mb-4">
                        <Upload className="w-7 h-7 text-amber-600" />
                      </div>
                      <p className="text-sm font-medium text-zinc-700 mb-1">点击或拖拽上传文件</p>
                      <p className="text-xs text-zinc-400">支持 .xlsx、.xls、.csv 格式</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-xs font-medium text-zinc-500">已上传文件</p>
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-2xl"
                    >
                      <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <FileXls className="w-5 h-5 text-amber-600" weight="fill" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-amber-900 truncate">{uploadedFile.name}</p>
                        <p className="text-xs text-amber-600">
                          {uploadedFile.rowCount.toLocaleString()} 行 · {uploadedFile.columns.length} 列
                        </p>
                      </div>
                      <motion.button
                        onClick={handleRemoveFile}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="p-2 text-amber-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="移除文件"
                      >
                        <Trash className="w-4 h-4" />
                      </motion.button>
                    </motion.div>

                    {/* File schema preview */}
                    <div className="p-3 bg-zinc-50 rounded-xl border border-zinc-200">
                      <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-2">列结构</p>
                      <div className="flex flex-wrap gap-1.5">
                        {uploadedFile.columns.map((col) => (
                          <span
                            key={col}
                            className="px-2 py-1 text-[10px] bg-white border border-zinc-200 rounded-md text-zinc-600"
                          >
                            {col}
                          </span>
                        ))}
                        {/* {uploadedFile.columns.length > 8 && (
                          <span className="px-2 py-1 text-[10px] bg-zinc-100 rounded-md text-zinc-400">
                            +{uploadedFile.columns.length - 8}
                          </span>
                        )} */}
                      </div>
                    </div>
                  </div>
                )}

                {/* AI Input Section - Only show when file is uploaded */}
                {uploadedFile && (
                  <>
                    <div className="h-px bg-zinc-100" />
                    
                    {/* Input form */}
                    <form onSubmit={handleSubmit} className="space-y-3">
                      <label className="block">
                        <span className="text-xs font-medium text-zinc-500 mb-1.5 block">
                          {generatedNodes.length > 0 ? '描述修改需求（可选）' : '第二步：描述你的数据处理需求'}
                        </span>
                        <textarea
                          ref={textareaRef}
                          value={input}
                          onChange={(e) => setInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmit(e as unknown as React.FormEvent);
                          }}
                          placeholder={
                            generatedNodes.length > 0
                              ? '例如：再添加一个筛选条件，金额大于 5000；或者修改聚合方式为平均值...'
                              : `例如：筛选出金额大于 1000 的记录，按客户汇总后导出`
                          }
                          rows={4}
                          disabled={isParsing}
                          className="w-full text-sm text-zinc-700 bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-3 resize-none focus:outline-none focus:ring-2 focus:ring-amber-400/30 focus:border-amber-400 placeholder:text-zinc-400 transition-all disabled:opacity-50"
                        />
                      </label>

                      <div className="flex items-center gap-2">
                        <motion.button
                          type="submit"
                          disabled={!input.trim() || isParsing}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium bg-zinc-900 text-white rounded-xl hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          {isParsing ? (
                            <>
                              <Spinner className="w-4 h-4 animate-spin" weight="bold" />
                              AI 分析中…
                            </>
                          ) : (
                            <>
                              <Lightning className="w-4 h-4" weight="fill" />
                              {generatedNodes.length > 0 ? '修改工作流' : '生成工作流'}
                            </>
                          )}
                        </motion.button>

                        {(currentIntent || currentPlan) && (
                          <motion.button
                            type="button"
                            onClick={handleReset}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="p-2.5 rounded-xl border border-zinc-200 text-zinc-500 hover:border-zinc-300 hover:text-zinc-700 transition-colors"
                            title="重置"
                          >
                            <ArrowCounterClockwise className="w-4 h-4" />
                          </motion.button>
                        )}
                      </div>

                      <p className="text-[10px] text-zinc-400 text-center">
                        ⌘ Enter 快速发送
                      </p>
                    </form>

                    {/* Example prompts - Only show when no workflow generated */}
                    {!currentIntent && !currentPlan && generatedNodes.length === 0 && (
                      <div>
                        <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-2">示例需求</p>
                        <div className="flex flex-wrap gap-1.5">
                          {EXAMPLE_PROMPTS.map((p) => (
                            <motion.button
                              key={p}
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() => setInput(p)}
                              className="px-2.5 py-1 text-xs text-zinc-600 bg-zinc-50 hover:bg-amber-50 hover:text-amber-700 border border-zinc-200 hover:border-amber-200 rounded-full transition-colors"
                            >
                              {p}
                            </motion.button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Results */}
                    <AnimatePresence>
                      {(currentIntent || currentPlan || generatedNodes.length > 0) && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="space-y-3"
                        >
                          <div className="h-px bg-zinc-100" />

                          {/* Workflow Preview */}
                          {generatedNodes.length > 0 && (
                            <div className="space-y-1.5">
                              <div className="flex items-center justify-between">
                                <h4 className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">生成的流程图</h4>
                                <span className="text-[10px] text-zinc-400">{generatedNodes.length} 个节点</span>
                              </div>
                              <div className="p-3 bg-zinc-50 rounded-xl border border-zinc-200">
                                <div className="space-y-2">
                                  {generatedNodes.map((node, idx) => (
                                    <div key={node.id} className="flex items-center gap-2">
                                      <div className="w-6 h-6 rounded-md bg-amber-100 flex items-center justify-center flex-shrink-0">
                                        <span className="text-[10px] font-semibold text-amber-600">{idx + 1}</span>
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium text-zinc-700 truncate">{node.data.label}</p>
                                        <p className="text-[10px] text-zinc-400">{node.type}</p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}

                          {currentIntent && (
                            <div className="space-y-1.5">
                              <h4 className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">识别意图</h4>
                              <div className="p-3 bg-zinc-50 rounded-xl border border-zinc-100">
                                <div className="flex items-center gap-2 mb-1.5">
                                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-semibold rounded-full">
                                    {currentIntent.type}
                                  </span>
                                </div>
                                <p className="text-xs text-zinc-700 leading-relaxed">{currentIntent.goal}</p>
                              </div>
                            </div>
                          )}

                          {currentPlan && (
                            <div className="space-y-1.5">
                              <h4 className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">执行计划</h4>
                              <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-100">
                                <pre className="text-[11px] text-emerald-800 whitespace-pre-wrap font-mono leading-relaxed">
                                  {currentPlan.description}
                                </pre>
                              </div>
                            </div>
                          )}

                          {/* Action buttons */}
                          <div className="flex items-center gap-2">
                            <motion.button
                              onClick={handleApply}
                              disabled={applied}
                              whileHover={{ scale: applied ? 1 : 1.02 }}
                              whileTap={{ scale: applied ? 1 : 0.98 }}
                              className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold rounded-xl transition-all ${
                                applied
                                  ? 'bg-emerald-500 text-white cursor-default'
                                  : 'bg-amber-500 hover:bg-amber-600 text-white'
                              }`}
                            >
                              {applied ? (
                                <>
                                  <CheckCircle className="w-4 h-4" weight="fill" />
                                  已应用到画布
                                </>
                              ) : (
                                <>
                                  <PaperPlaneRight className="w-4 h-4" weight="fill" />
                                  应用到工作流画布
                                </>
                              )}
                            </motion.button>

                            {!applied && (
                              <motion.button
                                onClick={handleModifyWorkflow}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="p-3 rounded-xl border border-zinc-200 text-zinc-500 hover:border-amber-300 hover:text-amber-600 transition-colors"
                                title="继续修改"
                              >
                                <ChatTeardropText className="w-4 h-4" />
                              </motion.button>
                            )}
                          </div>

                          {!applied && (
                            <p className="text-[10px] text-zinc-400 text-center">
                              对生成的流程不满意？点击&quot;继续修改&quot;按钮描述修改需求
                            </p>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-zinc-100 bg-zinc-50/50">
              <p className="text-[10px] text-zinc-400 text-center">
                AI 生成的工作流仅供参考，请在画布中检查并调整节点配置
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
